import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";

import Icon from "./Icon";
import OptionsMenu from "./OptionsMenu";

const OptionsIcon = styled(Icon)`
    font-size: 2em;
    margin-left: 0.5em;
    padding: 5px;
    border-radius: 5px;

    &:hover {
        background-color: #f2f2f2;
    }
`;

const dropdownMarginLeft = 15;

const calculatePosition = (iconRef) => {
    if (!iconRef.current) {
        return { top: 0, left: 0 };
    }

    const rect = iconRef.current.getBoundingClientRect();

    return {
        top: rect.top,
        left: rect.right + dropdownMarginLeft,
    };
};

/**
 * Dropdown button exposing edit geometry, dim test, and settings options for a group.
 *
 * @param root0 - The component props.
 * @param root0.onEditGeometry - Callback to edit the group geometry.
 * @param root0.onStartDimTest - Callback to start a dim test.
 * @param root0.dimTestsDisabled - Whether dim tests are disabled.
 * @param root0.settingsDisabled - Whether settings are disabled.
 * @param root0.onOpenSettings - Callback to open the settings modal.
 * @param root0.geometryEditable - Whether the geometry can be edited.
 * @returns The rendered options button.
 */
const OptionsButton = ({
    onEditGeometry,
    onStartDimTest,
    dimTestsDisabled,
    settingsDisabled,
    onOpenSettings,
    geometryEditable = true,
}) => {
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
    });
    const iconRef = useRef(null);
    const dropdownRef = useRef(null);

    const toggleDropdown = () => {
        if (!isDropdownOpen) {
            setDropdownPosition(calculatePosition(iconRef));
        }

        setDropdownOpen(!isDropdownOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                iconRef.current &&
                !iconRef.current.contains(event.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen]);

    return (
        <>
            <OptionsIcon
                ref={iconRef}
                className="fa fa-ellipsis-v"
                onClick={toggleDropdown}
            />
            {isDropdownOpen &&
                ReactDOM.createPortal(
                    <OptionsMenu
                        dropdownRef={dropdownRef}
                        dropdownPosition={dropdownPosition}
                        onEditGeometry={onEditGeometry}
                        onStartDimTest={onStartDimTest}
                        dimTestsDisabled={dimTestsDisabled}
                        settingsDisabled={settingsDisabled}
                        onOpenSettings={onOpenSettings}
                        geometryEditable={geometryEditable}
                    />,
                    document.body,
                )}
        </>
    );
};

OptionsButton.propTypes = {
    onEditGeometry: PropTypes.func,
    onStartDimTest: PropTypes.func,
    dimTestsDisabled: PropTypes.bool,
    settingsDisabled: PropTypes.bool,
    onOpenSettings: PropTypes.func,
    geometryEditable: PropTypes.bool,
};

export default OptionsButton;
